import { useState } from "react";
import { motion } from "framer-motion";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { ParkettStar } from "../components/ParkettStar";
import { useSeo } from "../hooks/useSeo";
import { IMG, PARKETT_STARS } from "../data/content";
import { FileText, Award } from "lucide-react";

const SEALS = [
  {
    key: "tuev",
    img: IMG.tuev,
    imgAlt: "TÜV Rheinland – Fachbetrieb für Fußbodentechnik",
    title: "TÜV Rheinland zertifiziert",
    sub: "2012 als erster Betrieb in Hessen als „Fachbetrieb für Fußbodentechnik“ ausgezeichnet.",
  },
  {
    key: "boden-zukunft",
    img: IMG.bodenZukunft,
    imgAlt: "Ausgezeichnet mit dem Boden Zukunft Award",
    title: "Boden Zukunft Award 2024",
    sub: "Ausgezeichnet von den Fachzeitschriften BTH Heimtex, FussbodenTechnik und Parkett Magazin.",
  },
  {
    key: "ncheck",
    img: IMG.sustainSeal,
    imgAlt: "Nachhaltigkeit im Handwerk 360°",
    title: "NCheck-360",
    sub: "Erfolgreich absolvierte Nachhaltigkeits-Zertifizierung im Handwerk.",
  },
];

const STAR_DETAILS = {
  "2024": {
    lead: "Vorbildlicher Ausbildungsbetrieb im Handwerk",
    blocks: [
      { h: "Azubis mit Respekt fürs Handwerk begeistern", p: "Fussboden Blum hat bereits mehr als 25 junge Talente erfolgreich ausgebildet. Doch in diesem Jahr war die Unterzeichnung von Lehrverträgen durch gleich vier angehende Parkettleger auf einmal auch für die erfahrenen Ausbilder außergewöhnlich. Dieser Erfolg wurde durch innovative Wege der Auszubildenden-Werbung, großzügige übertarifliche Löhne, zahlreiche Benefits und die Fähigkeit, die eigene Begeisterung für das Handwerk an die nächste Generation weiterzugeben, ermöglicht. Die Begeisterung übertrug sich sogar auf die Parkett-Star-Jury." },
      { h: "Fachliches Know-How als Motivationsfaktor", p: "Die angeführten zusätzlichen Leistungen und Angebote bilden die Grundlage, um junge Menschen für das Handwerk zu begeistern. „Die Ausbildung zum Parkettleger ist äußerst vielseitig“, hebt Blum hervor. „Unser Beruf bietet zahlreiche Facetten, die wir den Auszubildenden während der dreijährigen Ausbildung näherbringen.“ Als Prüfer im Gesellenprüfungsausschuss ist er mit den Inhalten der Ausbildung vertraut und setzt sich immer wieder aufs Neue damit auseinander. Dabei hat er festgestellt, dass nicht alle Ausbildungsbetriebe in der Lage sind, sämtliche Ausbildungsinhalte zu vermitteln. „Bei manchen gehören bestimmte Tätigkeiten nun mal nicht zum Portfolio. Nicht so bei uns. Wir sind in der Lage, alle relevanten Ausbildungstätigkeiten an unsere Auszubildenden weiterzugeben und gehen überdies auf weitere, angrenzende Fachkompetenzen ein.“" },
      { p: "Selbst alte Techniken wie das Verspannen von Teppichböden werden auf so mancher Baustelle noch an den Fachkräftenachwuchs weitergegeben. Über das bloße Vermitteln der Ausbildungsinhalte hinaus hat der zweifache Familienvater außerdem den Anspruch, das Fachwissen und die handwerklichen Fähigkeiten seiner Mitarbeiter durch regelmäßige Fortbildungen zu fördern und weiterzuentwickeln. „Wir bleiben als Betrieb immer auf dem neuesten Stand der Technik, kennen die aktuellen Maschinen und Werkzeuge oder lernen Tricks und Kniffe der Besten aus unserer Branche und können diese für uns umsetzen.“" },
    ],
    thanks: "Unser Dank gilt allen Mitarbeitenden sowie der SN-Fachpresse / der Fachzeitschrift ParkettMagazin.",
    urkundeImg: "/urkunden/parkett-star-2024.png",
    urkundePdf: "/downloads/parkett-star-2024-urkunde.pdf",
    artikelPdf: "/downloads/parkett-star-2024-artikel.pdf",
    fotos: [
      { src: "https://customer-assets-0z36b82j.emergentagent.net/job_fussboden-modern/artifacts/jo7iz36i_Fussboden-Blum-Parkett-Star-2024.jpg", alt: "Verleihung des Parkett Star 2024 – Konstantin Blum mit Urkunde und Pokal" },
      { src: "https://customer-assets-0z36b82j.emergentagent.net/job_fussboden-modern/artifacts/lkr12vfw_Fussboden-Blum-Parkett-Star-2024-2.jpg", alt: "Gruppenbild aller Preisträger des Parkett Star 2024 auf der DOMOTEX-Bühne" },
      { src: "https://customer-assets-0z36b82j.emergentagent.net/job_fussboden-modern/artifacts/xbwihmor_Fussboden-Blum-Parkett-Star-2024-3.jpg", alt: "Die Geschäftsführer Michael und Konstantin Blum" },
      { src: "https://customer-assets-0z36b82j.emergentagent.net/job_fussboden-modern/artifacts/weck8kp2_Fussboden-Blum-Parkett-Star-2024-4.jpg", alt: "Das Team von Fussboden Blum mit den vier Auszubildenden" },
    ],
  },
  "2021": {
    lead: "Vorbildlicher Generationenwechsel im Handwerk",
    blocks: [
      { p: "Die Nachfolge in Familienunternehmen gestaltet sich oftmals schwierig und einen Nachfolger aus der eigenen Familie zu haben, ist daher ein großes Glück. Bei Fußboden Blum verdoppelt sich dieses Glück, weil beide Generationen auf Augenhöhe voneinander profitieren können – von der Erfahrung der Älteren und den neuen Ideen des Jüngeren. Die Parkett-Star-Jury hat Michael und Konstantin Blum für den vorbildlichen Generationswechsel mit dem Parkett Star 2021 ausgezeichnet." },
      { h: "Die Nachfolge macht Gutes noch besser", p: "Die Nachfolge von der zweiten auf die dritte Generation wurde im Januar 2019 amtlich: Im Jahr des 50-jährigen Firmenjubiläums übertrug Michael Blum seinem Sohn Konstantin einen signifikanten Unternehmensanteil, beide wurden geschäftsführende Gesellschafter und leiten die Geschicke ihres Handwerksbetriebes seither gemeinsam. Auch wenn der Juniorchef zu diesem Zeitpunkt schon in viele Aufgaben hineingewachsen war, ging rückblickend dann doch alles ziemlich schnell. In knapp 3,5 Jahren hatte Konstantin nach dem Wirtschaftsabitur bei einem befreundeten Fachbetrieb zunächst die Ausbildung zum Parkettleger absolviert, gewann als Bester seines Jahrgangs beim Bundesleistungswettbewerb und schloss in einem trialen Studium die Meisterprüfung, den Betriebswirt des Handwerks und den Bachelor of Arts im Handwerksmanagement erfolgreich ab. Als 2014 ein Bauleiterposten im elterlichen Betrieb vakant wurde, entschied er sich schließlich früher als geplant für den Einstieg in das Familienunternehmen." },
      { p: "Inzwischen beschäftigt Fußboden Blum 29 Mitarbeiter, davon 20 Parkett- und Bodenleger auf den Baustellen. Zurzeit absolvieren zudem vier angehende Parkettleger und eine Bürokauffrau bei Blum die Lehre." },
      { h: "Die neuen Ideen tun dem Betrieb gut", p: "Das gemeinsame Ziel ist, die Digitalisierung des Betriebes Schritt für Schritt voranzubringen. Zuletzt wurden in diesem Bereich sowohl die Neugestaltung des Firmenlogos als auch der Launch der neuen Homepage unternommen. Dabei geht es auch um Prozessoptimierung: „Wir können an vielen kleinen Stellschrauben drehen, um effektiver zu werden. Durch geänderte Arbeitsabläufe und den Einsatz spezieller Produkte und Maschinen lassen sich Prozesse oft vereinfachen und Zeit sparen.“ Immerhin gilt es, Tag für Tag mit 20 Verlegern, einem Dutzend Fahrzeugen und sämtlichen erforderlichen Maschinen, Werkzeugen und Materialien die Termine für alle Baustellen so wirtschaftlich wie möglich zu koordinieren." },
    ],
    thanks: "Wir danken an dieser Stelle der SN-Fachpresse / der Fachzeitschrift ParkettMagazin Hamburg.",
    urkundeImg: "/urkunden/parkett-star-2021.png",
    urkundePdf: "/downloads/parkett-star-2021-urkunde.pdf",
    artikelPdf: "/downloads/parkett-star-2021-artikel.pdf",
    fotos: [
      { src: "https://customer-assets-0z36b82j.emergentagent.net/job_fussboden-modern/artifacts/ev3mba5h_Fussboden-Blum_Parkett-Star-2021.jpg", alt: "Das Team von Fussboden Blum an der Firmenstele in Mittelkalbach" },
      { src: "https://customer-assets-0z36b82j.emergentagent.net/job_fussboden-modern/artifacts/k0ak8pgx_Fussboden-Blum-Generationenwechsel-Auszeichnung-Parkett-Star-2021.jpg", alt: "Generationenwechsel: Konstantin und Michael Blum in der Ausstellung in Mittelkalbach" },
    ],
  },
  "2016": {
    lead: "Vorbildliche Mitarbeiterqualifizierung im Handwerk",
    blocks: [
      { p: "Aus- und Weiterbildung sind für Fussboden Blum die Basis für Qualität im Handwerk. Fortbildungen sind deshalb ebenfalls Standard und werden mit einem Schulungsplan in den Betriebsablauf integriert. Diese Konsequenz belohnt die Parkett-Star-Jury mit dem Parkett Star 2016 für vorbildliche Mitarbeiterqualifizierung. Mit einer Zertifizierung beim FHR und TÜV Rheinland qualifizierte sich Fussboden Blum zum Fachbetrieb für Fußbodentechnik, dem – zu diesem Zeitpunkt – ersten in Hessen." },
      { h: "Qualität kommt von Qualifizierung", p: "Fussböden aus Meisterhand – das ist ein Versprechen, das uns sehr wichtig ist. Um allerdings einen hohen Qualitätsstandard bieten zu können, müssen umgekehrt alle Mitarbeiter einen hohen Wissensstand haben und das lässt sich nur über regelmäßige Fort- und Weiterbildungen erreichen. Die Technik bleibt dabei ebenfalls nicht stehen – neue Materialien, neue Abläufe, geänderte Vorschriften, erweiterte Kontrollen machen Updates unumgänglich." },
      { p: "Gemeinsame Fortbildungen stärken das Gemeinschaftsgefühl und spornen an, auch die Geschäftsführung selbst ist davon nicht ausgeschlossen. Damit alle Mitarbeitenden einbezogen werden können, ist es zudem effektiver, Schulungen als Tagesprogramm für die gesamte Mannschaft im eigenen Betrieb zu organisieren, ebenso Betriebsfahrten mit allen Mitarbeitern zu Herstellern. Dann ist nur ein Tag geblockt und alle haben den gleichen Wissensstand in der Theorie und vor allem der praktischen Anwendung von neuen Produkten und Maschinen." },
    ],
    thanks: "Auch hier danken wir explizit der SN-Fachpresse / der Fachzeitschrift ParkettMagazin Hamburg.",
    urkundeImg: "/urkunden/parkett-star-2016.png",
    urkundePdf: "/downloads/parkett-star-2016-urkunde.pdf",
    artikelPdf: "/downloads/parkett-star-2016-artikel.pdf",
    fotos: [
      { src: "https://customer-assets-0z36b82j.emergentagent.net/job_fussboden-modern/artifacts/5sxevwtd_Fussboden-Blum-ParkettStar2016-Preisverleihung.jpg", alt: "Preisverleihung Parkett Star 2016 – Urkunde und Pokal für Fussboden Blum" },
      { src: "https://customer-assets-0z36b82j.emergentagent.net/job_fussboden-modern/artifacts/58a9xd5w_Fussboden-Blum-Parkett-Star-2016-Fortbildung.jpg", alt: "Betriebsfahrt zur Fortbildung beim Hersteller Forbo" },
      { src: "https://customer-assets-0z36b82j.emergentagent.net/job_fussboden-modern/artifacts/4vbxvlf6_Fussboden-Blum-Parkett-Star-2016-Teambild.jpg", alt: "Das Team von Fussboden Blum 2016 vor dem Firmenschild" },
    ],
  },
};

const AuszeichnungenHero = () => (
  <section data-testid="auszeichnungen-hero" className="relative min-h-[68vh] flex items-end overflow-hidden">
    <img
      src={IMG.praemiertHero}
      alt="Goldener Stern – Auszeichnungen von Fussboden Blum"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#2B2B30]/95 via-[#2B2B30]/55 to-[#2B2B30]/30" />
    <div className="container-blum relative z-10 pb-16 pt-40">
      <Reveal>
        <p data-testid="auszeichnungen-hero-script" className="font-script text-3xl lg:text-4xl text-[#D7C4B1]">
          Qualität, die anerkannt wird
        </p>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight mt-3 max-w-3xl">
          Auszeichnungen
        </h1>
        <p className="text-lg text-white/85 mt-5 max-w-2xl leading-relaxed">
          Parkett Star, TÜV Rheinland, Boden Zukunft Award – ein Überblick über
          die Auszeichnungen und Zertifizierungen unseres Meisterbetriebs.
        </p>
      </Reveal>
    </div>
  </section>
);

/* Wiederverwendbarer Detail-Block pro Auszeichnung:
   Kurztext + Bild(er)/Urkunde + Button „Artikel" (PDF).
   images: [{ src, alt, contain?, caption? }] – contain für Hochformat-Urkunden. */
const AwardDetail = ({ overline, title, children, images, pdf, reverse = false, bordered = false, testid }) => (
  <section data-testid={testid} className={`py-20 lg:py-28 ${bordered ? "border-t border-[#E5E0D8]" : ""}`}>
    <div className="container-blum">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
        <div className={reverse ? "lg:order-2" : ""}>
          <Reveal><p className="overline">{overline}</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4 leading-tight">{title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="text-[#6B6B73] text-lg leading-relaxed mt-6 space-y-5">{children}</div>
          </Reveal>
          {pdf && (
            <Reveal delay={0.15}>
              <a
                href={pdf}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`${testid}-artikel`}
                className="btn-base btn-red mt-8"
              >
                Artikel <FileText className="w-4 h-4" />
              </a>
            </Reveal>
          )}
        </div>
        <div className={`space-y-5 ${reverse ? "lg:order-1" : ""}`}>
          {images.map((img, i) => (
            <Reveal key={i} delay={0.1 + i * 0.05}>
              {img.contain ? (
                <figure>
                  <div className="bg-white border border-[#E5E0D8] p-4 lg:p-6">
                    <img
                      src={img.src}
                      alt={img.alt}
                      data-testid={`${testid}-bild-${i}`}
                      className="w-full h-auto max-h-[560px] object-contain mx-auto"
                    />
                  </div>
                  {img.caption && (
                    <figcaption className="text-sm text-[#6B6B73] mt-3 flex items-center gap-3">
                      <span className="w-6 h-px bg-[#D32F2F] shrink-0" aria-hidden="true" />
                      {img.caption}
                    </figcaption>
                  )}
                </figure>
              ) : (
                <img
                  src={img.src}
                  alt={img.alt}
                  data-testid={`${testid}-bild-${i}`}
                  className="w-full aspect-[4/3] object-cover border border-[#E5E0D8]"
                />
              )}
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* Interaktive Parkett-Star-Übersicht: Klick auf einen Stern öffnet
   die Inhalte des Jahres im Panel darunter (Akkordeon-Logik). */
const ParkettStarAccordion = () => {
  const [active, setActive] = useState("2024");
  const d = STAR_DETAILS[active];

  return (
    <section data-testid="auszeichnungen-stars" className="py-20 lg:py-28 bg-white border-t border-[#E5E0D8]">
      <div className="container-blum">
        <div className="max-w-3xl mb-12">
          <Reveal><p className="overline">Parkett Star</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4 leading-tight">
              Dreifach ausgezeichnet vom Parkett Magazin
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[#6B6B73] text-lg leading-relaxed mt-5">
              Seit über 50 Jahren sind wir Ihr Fachbetrieb für Parkett und Fußbodentechnik. Neben
              einer professionellen und kundenorientierten Arbeit legen wir großen Wert auf
              Menschlichkeit und gegenseitigen Respekt. Diesen Fokus werden und wollen wir auch
              zukünftig beibehalten. Gleichermaßen freuen wir uns ganz besonders über die
              Auszeichnungen für den Parkett Star 2016, 2021 und 2024, die alles andere als
              selbstverständlich sind und die wir unseren Kunden, Partnern und Seitenbesuchern
              nicht vorenthalten wollen.
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {PARKETT_STARS.map((s, i) => {
            const isActive = active === s.year;
            return (
              <Reveal key={s.year} delay={i * 0.08}>
                <button
                  onClick={() => setActive(s.year)}
                  aria-pressed={isActive}
                  data-testid={`auszeichnung-star-${s.year}`}
                  className={`relative w-full flex flex-col items-center text-center border p-8 lg:p-10 h-full transition-colors ${
                    isActive
                      ? "bg-white border-[#D32F2F]"
                      : "bg-[#F9F8F6] border-[#E5E0D8] hover:border-[#D32F2F]/50"
                  }`}
                >
                  <span
                    className={`absolute top-0 left-0 h-1 bg-[#D32F2F] transition-all duration-300 ${isActive ? "w-full" : "w-0"}`}
                    aria-hidden="true"
                  />
                  <ParkettStar year={s.year} size={140} />
                  <h3 className="font-display text-2xl font-bold text-[#2B2B30] mt-6">Parkett Star {s.year}</h3>
                  <p className="text-[#6B6B73] mt-2 leading-relaxed">{s.area}</p>
                  <span className={`text-xs font-display uppercase tracking-[0.15em] mt-4 ${isActive ? "text-[#D32F2F]" : "text-[#6B6B73]"}`}>
                    {isActive ? "Ausgewählt" : "Details ansehen"}
                  </span>
                </button>
              </Reveal>
            );
          })}
        </div>

        {/* Detail-Panel des gewählten Jahres */}
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          data-testid="star-panel"
          className="mt-10 border border-[#E5E0D8] bg-white"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start p-6 lg:p-10">
            <div>
              <p className="overline">Parkett Star {active}</p>
              <h3 className="font-display text-2xl lg:text-3xl font-bold text-[#2B2B30] mt-3 leading-tight">
                {d.lead}
              </h3>
              <div className="text-[#6B6B73] leading-relaxed mt-6 space-y-5">
                {d.blocks.map((b, i) => (
                  <div key={i}>
                    {b.h && (
                      <h4 className="font-display text-lg font-semibold text-[#2B2B30] mb-1.5">{b.h}</h4>
                    )}
                    <p>{b.p}</p>
                  </div>
                ))}
                <p className="text-sm italic text-[#8B8578] border-l-2 border-[#D7C4B1] pl-4">
                  {d.thanks}
                </p>
              </div>
              <div className="flex flex-wrap gap-4 mt-8">
                <a
                  href={d.urkundePdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="star-panel-urkunde"
                  className="btn-base btn-outline"
                >
                  Urkunde <Award className="w-4 h-4" />
                </a>
                {d.artikelPdf && (
                  <a
                    href={d.artikelPdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="star-panel-artikel"
                    className="btn-base btn-red"
                  >
                    Artikel <FileText className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
            <div>
              <figure>
                <div className="bg-[#F9F8F6] border border-[#E5E0D8] p-4 lg:p-6">
                  <img
                    src={d.urkundeImg}
                    alt={`Urkunde Parkett Star ${active} für die Fussboden Blum GmbH`}
                    data-testid="star-panel-urkunde-img"
                    className="w-full h-auto max-h-[520px] object-contain mx-auto"
                  />
                </div>
                <figcaption className="text-sm text-[#6B6B73] mt-3 flex items-center gap-3">
                  <span className="w-6 h-px bg-[#D32F2F] shrink-0" aria-hidden="true" />
                  Urkunde zum Parkett Star {active}
                </figcaption>
              </figure>
              {d.fotos.length > 0 && (
                <div className="grid grid-cols-2 gap-3 mt-5">
                  {d.fotos.map((f, i) => (
                    <img key={i} src={f.src} alt={f.alt} className="w-full aspect-[4/3] object-cover border border-[#E5E0D8]" />
                  ))}
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default function Auszeichnungen() {
  useSeo(
    "Auszeichnungen – Fussboden Blum",
    "Auszeichnungen und Zertifizierungen von Fussboden Blum: Parkett Star, TÜV Rheinland und Boden Zukunft Award – anerkannte Qualität aus Kalbach."
  );

  return (
    <div data-testid="auszeichnungen-page">
      <Navbar />
      <main>
        <AuszeichnungenHero />

        <AwardDetail
          testid="detail-boden-zukunft"
          overline="Auszeichnung im Detail"
          title="Boden Zukunft Award 2024"
          pdf="/downloads/gewinnerportrait-boden-zukunft-award-2024.pdf"
          images={[
            { src: IMG.awardTrophy, alt: "Die Trophäe des Boden Zukunft Awards 2024 – eine gestapelte Holzpyramide" },
            { src: IMG.awardVerleihung, alt: "Verleihung des Boden Zukunft Award 2024 auf der Messe" },
          ]}
        >
          <p>
            Zum ersten Mal verliehen die Fachzeitschriften BTH Heimtex, FussbodenTechnik und
            Parkett Magazin im Jahr 2024 den Boden Zukunft Award – und die Fussboden Blum GmbH
            zählt zu den Preisträgern. Ausgezeichnet wurde unser ganzheitlicher
            Nachhaltigkeitsansatz, der den „Faktor Mensch“ in den Mittelpunkt stellt:
            Ergonomische Hilfsmittel wie elektrische Treppensteiger schonen Rücken und Knie, ein
            betriebliches Gesundheitsmanagement mit Coachings und Haltungsanalysen,
            Krankenzusatzversicherung und E-Bike-Leasing stärken Gesundheit und Motivation
            unseres Teams.
          </p>
          <p>
            Auch ökologisch gehen wir voran – etwa mit schadstoffarmen Materialien und einem
            Pilotprojekt zum Recycling alter Linoleumböden. „Die Beschäftigten sind unsere
            wichtigste Ressource“, betont Geschäftsführer Konstantin Blum. Nicht zuletzt sichert
            unsere überdurchschnittliche Ausbildungsvergütung samt Zusatzleistungen den
            Fachkräftenachwuchs – für uns eine nachhaltige Investition in die Zukunft.
          </p>
        </AwardDetail>

        <AwardDetail
          testid="detail-ncheck"
          overline="Zertifizierung im Detail"
          title="Nachhaltigkeit im Handwerk 360°"
          pdf="/downloads/nachhaltigkeit-mit-weitblick.pdf"
          reverse
          bordered
          images={[
            {
              src: IMG.ncheckUrkunde,
              alt: "Teilnahmeurkunde Nachhaltigkeit im Handwerk 360° für die Fußboden Blum GmbH",
              contain: true,
              caption: "Teilnahme am 14.05.2025 in Kalbach – ausgestellt von der Handwerkskammer Kassel",
            },
          ]}
        >
          <p>
            Fußboden Blum geht den nächsten Schritt in Sachen Zukunftsfähigkeit: Mit dem
            erfolgreichen „Nachhaltigkeit 360°“-Check stellt das Unternehmen sein ökologisches,
            soziales und wirtschaftliches Engagement auf den Prüfstand – und beweist, dass
            Verantwortung hier mehr ist als ein Trend.
          </p>
          <p>
            Der Check auf Grundlage der Sustainable Development Goals (SDGs) der UN schafft die
            Perspektive und Grundlage für eine nachhaltige Betriebsentwicklung – regional,
            langlebig und reparierbar statt wegwerfbar.
          </p>
        </AwardDetail>

        <ParkettStarAccordion />

        {/* Weitere Siegel */}
        <section data-testid="auszeichnungen-seals" className="py-20 lg:py-28">
          <div className="container-blum">
            <div className="max-w-2xl mb-12">
              <Reveal><p className="overline">Zertifizierungen & Preise</p></Reveal>
              <Reveal delay={0.05}>
                <h2 className="text-3xl lg:text-4xl font-bold tracking-tight mt-4 leading-tight">
                  Weitere Auszeichnungen
                </h2>
              </Reveal>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {SEALS.map((s, i) => (
                <Reveal key={s.key} delay={i * 0.08}>
                  <div
                    data-testid={`auszeichnung-${s.key}`}
                    className="flex flex-col items-center text-center bg-white border border-[#E5E0D8] p-8 lg:p-10 h-full"
                  >
                    <div className="h-[100px] flex items-center justify-center">
                      <img src={s.img} alt={s.imgAlt} className="max-h-full w-auto" />
                    </div>
                    <h3 className="font-display text-xl font-bold text-[#2B2B30] mt-6">{s.title}</h3>
                    <p className="text-[#6B6B73] mt-2 leading-relaxed">{s.sub}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
